// Runtime loader for the baked cowboy files (public/assets/cowboys/<name>.cby, see bake.ts).
// One file per class: indexed skinned mesh (body + pack + gun in one atlas), LOD1 index, rest
// skeleton, sockets and retargeted Mixamo clips as int16 local quaternions. Loads async; the
// view shows nothing for a class until its asset arrives, and never throws on failure.
import * as THREE from 'three';

export interface BoneDef { n: string; p: number; pos: number[]; q: number[]; w: number[]; wq: number[]; aq: number[] }
export interface ClipData { name: string; frames: number; loop: boolean; dur: number; q: Int16Array; hips: Float32Array; /** horizontal root travel per cycle (m), stripped from hips at bake */ travel?: number }
export interface CowboyAsset {
  name: string;
  height: number;
  pistol: boolean;
  bones: BoneDef[];
  nB: number; // character bones; the gun bone is index nB
  geo0: THREE.BufferGeometry;
  geo1: THREE.BufferGeometry;
  tris0: number;
  tris1: number;
  map: THREE.Texture;
  mask: THREE.Texture;
  clips: Map<string, ClipData>;
  fps: number;
  idx: Record<string, number>; // named bone indices
  grip: { bone: number; pos: THREE.Vector3; q: THREE.Quaternion };
  muzzle: THREE.Vector3;
  nozzles: { pos: THREE.Vector3; dir: THREE.Vector3; r: number }[];
  pack: { min: THREE.Vector3; max: THREE.Vector3 };
  backZ: number;
  shoulderW: number;
  /** T-pose (aligned rest) as local quats, for procedural poses */
  tpose: Float32Array;
  /** per-bone aligned-rest world quat (model space): converts model-space deltas to bone-local */
  aq: THREE.Quaternion[];
  bindWq: THREE.Quaternion[];
  bindW: THREE.Vector3[];
}

export const CLASS_FILES = ['gunslinger', 'valkyrie', 'phantom', 'juggernaut'];
const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const cache = new Map<string, Promise<CowboyAsset | null>>();

export function loadCowboyAsset(cls: number, quality: number): Promise<CowboyAsset | null> {
  const name = CLASS_FILES[((cls % 4) + 4) % 4];
  const hi = quality >= 2;
  const key = name + (hi ? '' : '_lo');
  let p = cache.get(key);
  if (!p) {
    p = load(name, hi).catch((e) => { console.warn('[cowboys] asset failed', name, e); return null; });
    cache.set(key, p);
  }
  return p;
}

async function loadTex(url: string, srgb: boolean) {
  const tex = await new THREE.TextureLoader().loadAsync(url);
  tex.flipY = false;
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.anisotropy = 4;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/**
 * The pack's vertices: rigid on Spine2 and connected to geometry behind the torso's back (the baker's vertex ranges
 * index the UNwelded buffers, so they can't be used here). The pack, its kit and its nozzles are separate islands,
 * each with some vertex well behind the back; the torso's own Spine2-rigid skin never is.
 */
function findPack(P: Float32Array, si: Uint8Array, sw: Uint8Array, idx: ArrayLike<number>, sp2: number, backZ: number): Uint8Array {
  const n = P.length / 3, rigid = new Uint8Array(n), mark = new Uint8Array(n);
  for (let i = 0; i < n; i++) rigid[i] = si[i * 4] === sp2 && sw[i * 4] >= 250 ? 1 : 0;
  const stack: number[] = [];
  for (let i = 0; i < n; i++) if (rigid[i] && P[i * 3 + 2] < backZ - 0.04) { mark[i] = 1; stack.push(i); }
  // adjacency through triangles, flood over rigid verts
  const adj: number[][] = [];
  for (let t = 0; t < idx.length; t += 3) {
    const a = idx[t], b = idx[t + 1], c = idx[t + 2];
    if (!(rigid[a] && rigid[b] && rigid[c])) continue;
    (adj[a] ??= []).push(b, c); (adj[b] ??= []).push(a, c); (adj[c] ??= []).push(a, b);
  }
  while (stack.length) { const v = stack.pop()!; for (const u of adj[v] ?? []) if (!mark[u]) { mark[u] = 1; stack.push(u); } }
  return mark;
}

/**
 * The baker centres the pack on its outermost back face, which the side greebles skew: the gunslinger's shell sat
 * ~4 cm toward his left. Recentre it on the spine from the shell's own middle (x extent of its body-side half,
 * mid-height). Returns the shift (m).
 */
function centrePack(P: Float32Array, mark: Uint8Array, backZ: number, pack: { min: number[]; max: number[] }): number {
  const y0 = pack.min[1], y1 = pack.max[1], ym0 = y0 + (y1 - y0) * 0.25, ym1 = y0 + (y1 - y0) * 0.75;
  const zm = backZ + (pack.min[2] - backZ) * 0.6; // back is -z
  let l = Infinity, r = -Infinity;
  for (let i = 0; i < mark.length; i++) {
    if (!mark[i]) continue;
    const y = P[i * 3 + 1], z = P[i * 3 + 2];
    if (y < ym0 || y > ym1 || z < zm) continue;
    const x = P[i * 3]; if (x < l) l = x; if (x > r) r = x;
  }
  if (!Number.isFinite(l)) return 0;
  const dx = -(l + r) / 2;
  if (Math.abs(dx) < 0.003) return 0;
  for (let i = 0; i < mark.length; i++) if (mark[i]) P[i * 3] += dx;
  return dx;
}

/**
 * Seat the pack on the back after it moved down: lower, the torso is slimmer and the pack would hover. The baker
 * sat it flush at its old height, so move it in by however much the torso's back recedes between the two bands
 * (the back-most body point near the spine in each). Returns the z shift (m, + = toward the body).
 */
function seatPack(P: Float32Array, mark: Uint8Array, y0: number, y1: number, drop: number): number {
  if (drop <= 0) return 0;
  const back = (a: number, b: number) => {
    let bz = Infinity;
    for (let i = 0; i < mark.length; i++) {
      if (mark[i]) continue;
      const x = P[i * 3], y = P[i * 3 + 1], z = P[i * 3 + 2];
      if (y > a && y < b && Math.abs(x) < 0.1 && z < bz) bz = z;
    }
    return bz;
  };
  const was = back(y0 + drop + 0.1, y1 + drop - 0.1), now = back(y0 + 0.1, y1 - 0.1);
  if (!Number.isFinite(was) || !Number.isFinite(now)) return 0;
  const dz = Math.min(0.15, Math.max(0, now - was));
  for (let i = 0; i < mark.length; i++) if (mark[i]) P[i * 3 + 2] += dz;
  return dz;
}

/**
 * The source packs stand ~0.37-0.5 m off the back, so from the chase camera (behind and to one side) the far face
 * swings out past the shoulder and the pack reads as hung off-centre. Flatten it toward the back to a believable
 * depth: z behind `zf` is scaled by PACK_DEPTH (normals by the inverse). Returns the mapping for points on the pack.
 */
const PACK_DEPTH = 0.68;
function flattenPack(P: Float32Array, N: Int8Array, mark: Uint8Array, backZ: number, drop: number): (z: number) => number {
  const zf = backZ + 0.01;
  const map = (z: number) => (z < zf ? zf + (z - zf) * PACK_DEPTH : z);
  for (let i = 0; i < mark.length; i++) {
    if (!mark[i]) continue;
    P[i * 3 + 2] = map(P[i * 3 + 2]);
    P[i * 3 + 1] -= drop;
    let nx = N[i * 4], ny = N[i * 4 + 1], nz = N[i * 4 + 2] / PACK_DEPTH;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l; ny /= l; nz /= l;
    N[i * 4] = Math.round(nx * 127); N[i * 4 + 1] = Math.round(ny * 127); N[i * 4 + 2] = Math.round(nz * 127);
  }
  return map;
}

async function load(name: string, hi: boolean): Promise<CowboyAsset> {
  const root = BASE + 'assets/cowboys/' + name;
  const [buf, map, mask, xbuf] = await Promise.all([
    fetch(root + '.cby').then((r) => { if (!r.ok) throw new Error(r.statusText); return r.arrayBuffer(); }),
    loadTex(root + (hi ? '.jpg' : '_lo.jpg'), true),
    loadTex(root + '_mask.png', false),
    // the side file (bake.ts `extra` clips, e.g. the whiskey chug): optional, a cowboy without it just skips those
    fetch(root + '_x.cby').then((r) => (r.ok ? r.arrayBuffer() : null)).catch(() => null),
  ]);
  const dv = new DataView(buf);
  if (dv.getUint32(0, true) !== 0x31594243) throw new Error('bad cby');
  const jl = dv.getUint32(4, true);
  const h = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 8, jl)));
  const base = 8 + jl;
  const view = <T>(k: string, C: { new(b: ArrayBuffer, o: number, n: number): T; BYTES_PER_ELEMENT: number }) => {
    const [o, n] = h.buffers[k];
    return new C(buf.slice(base + o, base + o + n), 0, n / C.BYTES_PER_ELEMENT);
  };
  const big = h.verts > 65535;
  const geo0 = new THREE.BufferGeometry();
  const pos = view('pos', Float32Array);
  geo0.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const nrmA = view('nrm', Int8Array);
  const nrm = new THREE.BufferAttribute(nrmA, 4, true);
  geo0.setAttribute('normal', nrm);
  geo0.setAttribute('aoc', nrm); // same GL buffer: .w carries the baked AO (older bakes: 0 -> treated as open)
  geo0.setAttribute('uv', new THREE.BufferAttribute(view('uv', Uint16Array), 2, true));
  const si = view('si', Uint8Array), sw = view('sw', Uint8Array);
  const idx0 = big ? view('idx', Uint32Array) : view('idx', Uint16Array);
  // the pack gets its own bone (index nB + 1, after the gun bone) so the view can hold it square on the back
  // instead of riding every twist and lean of the chest
  const nBones = (h.bones as unknown[]).length;
  const packMark = findPack(pos, si, sw, idx0, h.spine2, h.backZ);
  const packDx = centrePack(pos, packMark, h.backZ, h.pack);
  // and seat its top below the collar, so the scarf's knot at the back of the neck can lie over it
  const neckY = h.bones[h.neck]?.w?.[1] ?? h.pack.max[1];
  const packDrop = Math.min(0.14, Math.max(0, h.pack.max[1] - (neckY - 0.1)));
  const packZ0 = flattenPack(pos, nrmA, packMark, h.backZ, packDrop);
  const packSeat = seatPack(pos, packMark, h.pack.min[1] - packDrop, h.pack.max[1] - packDrop, packDrop);
  const packZ = (z: number) => packZ0(z) + packSeat;
  for (let i = 0; i < packMark.length; i++) if (packMark[i]) si[i * 4] = nBones + 1;
  geo0.setAttribute('skinIndex', new THREE.BufferAttribute(si, 4));
  geo0.setAttribute('skinWeight', new THREE.BufferAttribute(sw, 4, true));
  const idx1 = big ? view('lod1', Uint32Array) : view('lod1', Uint16Array);
  geo0.setIndex(new THREE.BufferAttribute(idx0, 1));
  geo0.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, h.height * 0.5, 0), h.height);
  const geo1 = new THREE.BufferGeometry();
  for (const k of Object.keys(geo0.attributes)) geo1.setAttribute(k, geo0.attributes[k]);
  geo1.setIndex(new THREE.BufferAttribute(idx1, 1));
  geo1.boundingSphere = geo0.boundingSphere;

  const bones: BoneDef[] = h.bones;
  const nB = bones.length;
  const clips = new Map<string, ClipData>();
  (h.clips as { name: string; frames: number; loop: boolean; dur: number; travel?: number }[]).forEach((c, i) => {
    clips.set(c.name, { ...c, q: view('cq' + i, Int16Array), hips: view('ch' + i, Float32Array) });
  });
  if (xbuf) for (const [k, c] of readClips(xbuf)) clips.set(k, c);
  const idx: Record<string, number> = {};
  bones.forEach((b, i) => { idx[b.n] = i; });
  const aq = bones.map((b) => new THREE.Quaternion().fromArray(b.aq));
  const tpose = new Float32Array(nB * 4);
  const tq = new THREE.Quaternion();
  for (let i = 0; i < nB; i++) {
    const p = bones[i].p;
    if (p < 0) tq.copy(aq[i]); else tq.copy(aq[p]).invert().multiply(aq[i]);
    tq.toArray(tpose, i * 4);
  }
  return {
    name, height: h.height, pistol: h.pistol, bones, nB, geo0, geo1, tris0: idx0.length / 3, tris1: idx1.length / 3,
    map, mask, clips, fps: h.fps, idx,
    grip: { bone: h.gun.grip.bone, pos: new THREE.Vector3().fromArray(h.gun.grip.pos), q: new THREE.Quaternion().fromArray(h.gun.grip.q) },
    muzzle: new THREE.Vector3().fromArray(h.gun.muzzle),
    nozzles: (h.nozzles as { pos: number[]; dir: number[]; r?: number }[]).map((n) => ({ pos: new THREE.Vector3(n.pos[0] + packDx, n.pos[1] - packDrop, packZ(n.pos[2])), dir: new THREE.Vector3().fromArray(n.dir), r: n.r ?? 0.035 })),
    pack: { min: new THREE.Vector3(h.pack.min[0] + packDx, h.pack.min[1] - packDrop, packZ(h.pack.min[2])), max: new THREE.Vector3(h.pack.max[0] + packDx, h.pack.max[1] - packDrop, packZ(h.pack.max[2])) },
    backZ: h.backZ, shoulderW: h.shoulderW,
    tpose, aq,
    bindWq: bones.map((b) => new THREE.Quaternion().fromArray(b.wq)),
    bindW: bones.map((b) => new THREE.Vector3().fromArray(b.w)),
  };
}

/** clips from a side file (same container as the main bake, clips only) */
function readClips(buf: ArrayBuffer) {
  const out = new Map<string, ClipData>();
  const dv = new DataView(buf);
  if (buf.byteLength < 8 || dv.getUint32(0, true) !== 0x31594243) return out;
  const jl = dv.getUint32(4, true);
  const h = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 8, jl)));
  const base = 8 + jl;
  const view = <T>(k: string, C: { new(b: ArrayBuffer, o: number, n: number): T; BYTES_PER_ELEMENT: number }) => {
    const [o, n] = h.buffers[k];
    return new C(buf.slice(base + o, base + o + n), 0, n / C.BYTES_PER_ELEMENT);
  };
  (h.clips as { name: string; frames: number; loop: boolean; dur: number; travel?: number }[]).forEach((c, i) => {
    out.set(c.name, { ...c, q: view('cq' + i, Int16Array), hips: view('ch' + i, Float32Array) });
  });
  return out;
}

// ------------------------------------------------------------------ pose sampling (allocation-free)
const INV = 1 / 32767;
/**
 * Accumulate a clip's local quats at time t into acc (nB*4) with weight w, only for bones whose
 * mask entry is non-zero (mask value scales the weight). Quats are sign-aligned to acc's running
 * sum so nlerp blending is stable. Hips position goes to hipAcc.
 */
export function accumulate(c: ClipData, nB: number, fps: number, t: number, w: number, mask: Float32Array | null,
  acc: Float32Array, wsum: Float32Array, hipAcc: Float32Array, hipW: number) {
  if (w <= 0.0001) return;
  const last = c.frames - 1;
  let ft = t * fps;
  if (c.loop) { ft %= last; if (ft < 0) ft += last; } else ft = ft < 0 ? 0 : ft > last ? last : ft;
  const f0 = Math.floor(ft), f1 = Math.min(last, f0 + 1), a = ft - f0;
  const q = c.q;
  for (let i = 0; i < nB; i++) {
    const mw = mask ? mask[i] * w : w;
    if (mw <= 0.0001) continue;
    const o0 = (f0 * nB + i) * 4, o1 = (f1 * nB + i) * 4;
    let x1 = q[o1], y1 = q[o1 + 1], z1 = q[o1 + 2], w1 = q[o1 + 3];
    const x0 = q[o0], y0 = q[o0 + 1], z0 = q[o0 + 2], w0 = q[o0 + 3];
    if (x0 * x1 + y0 * y1 + z0 * z1 + w0 * w1 < 0) { x1 = -x1; y1 = -y1; z1 = -z1; w1 = -w1; }
    let x = (x0 + (x1 - x0) * a) * INV, y = (y0 + (y1 - y0) * a) * INV, z = (z0 + (z1 - z0) * a) * INV, ww = (w0 + (w1 - w0) * a) * INV;
    const o = i * 4;
    if (acc[o] * x + acc[o + 1] * y + acc[o + 2] * z + acc[o + 3] * ww < 0) { x = -x; y = -y; z = -z; ww = -ww; }
    acc[o] += x * mw; acc[o + 1] += y * mw; acc[o + 2] += z * mw; acc[o + 3] += ww * mw;
    wsum[i] += mw;
  }
  if (hipW > 0.0001) {
    const h = c.hips;
    hipAcc[0] += (h[f0 * 3] + (h[f1 * 3] - h[f0 * 3]) * a) * hipW;
    hipAcc[1] += (h[f0 * 3 + 1] + (h[f1 * 3 + 1] - h[f0 * 3 + 1]) * a) * hipW;
    hipAcc[2] += (h[f0 * 3 + 2] + (h[f1 * 3 + 2] - h[f0 * 3 + 2]) * a) * hipW;
    hipAcc[3] += hipW;
  }
}

/** add a static local pose (e.g. the T-pose) with weight w */
export function accumulateStatic(src: Float32Array, nB: number, w: number, mask: Float32Array | null, acc: Float32Array, wsum: Float32Array) {
  if (w <= 0.0001) return;
  for (let i = 0; i < nB; i++) {
    const mw = mask ? mask[i] * w : w;
    if (mw <= 0.0001) continue;
    const o = i * 4;
    let x = src[o], y = src[o + 1], z = src[o + 2], ww = src[o + 3];
    if (acc[o] * x + acc[o + 1] * y + acc[o + 2] * z + acc[o + 3] * ww < 0) { x = -x; y = -y; z = -z; ww = -ww; }
    acc[o] += x * mw; acc[o + 1] += y * mw; acc[o + 2] += z * mw; acc[o + 3] += ww * mw;
    wsum[i] += mw;
  }
}
